import {WalletPlatform} from '@/enums/WalletPlatform'
import {TWalletNetwork} from '@/model/wallets/types/WalletNetwork'
import {
  RejectedByUser,
  SdkNotInitializedError,
} from '@/model/wallets/types/WalletErrors'
import {ConnectionStrategy} from './ConnectionStrategy'
import {
  registerNeolineContextListeners,
  NeolineContextChangeHandler,
} from './NeolineContextListeners'
import {
  expectedNeoLineChainId,
  neoLineChainFamily,
  neoLineNameFamily,
  neoLineNetworkNameLabel,
  neoLineTargetLabel,
  validateNeoLineAddressNetwork,
  validateNeoLineExtensionChainId,
} from './NeolineNetworkValidation'
import {
  readNeoLineEvmChainId,
  readNeoLineNeoXDapiNetwork,
} from './NeolineNeoXEthereumConnection'

export type NeolineExtensionProviderContext = {
  type: 'neoline-extension'
  sdk: any
  address: string | null
}

export const isNeolineExtensionProvider = (
  provider: unknown
): provider is NeolineExtensionProviderContext =>
  typeof provider === 'object' &&
  provider !== null &&
  (provider as NeolineExtensionProviderContext).type === 'neoline-extension'

type NeolineExtensionConfig = {
  globalKey: 'NEOLineN3' | 'NEOLineNEOX'
  readyEvent: string
  accountChangedEvent: string
}

type NeolineNetworkReport = {
  chainId: number | undefined
  networkName: string | undefined
}

const REJECTION_TYPES = ['CANCELED', 'CANCELLED', 'CONNECTION_DENIED']

const isNeolineRejection = (error: any): boolean =>
  error?.code === 4001 ||
  REJECTION_TYPES.includes(String(error?.type ?? '').toUpperCase())

const configs: Record<'n3' | 'neoX', NeolineExtensionConfig> = {
  n3: {
    globalKey: 'NEOLineN3',
    readyEvent: 'NEOLine.N3.EVENT.READY',
    accountChangedEvent: 'NEOLine.N3.EVENT.ACCOUNT_CHANGED',
  },
  neoX: {
    globalKey: 'NEOLineNEOX',
    readyEvent: 'NEOLine.NEOX.EVENT.READY',
    accountChangedEvent: 'NEOLine.NEOX.EVENT.ACCOUNT_CHANGED',
  },
}

export class NeolineExtensionConnection implements ConnectionStrategy {
  private sdk: any = null
  private address: string | null = null
  private onAnotherNetwork = false
  private onContextChange?: NeolineContextChangeHandler
  private detachContextListeners?: () => void
  private readonly config: NeolineExtensionConfig

  constructor(
    private readonly implementation: 'n3' | 'neoX',
    // eslint-disable-next-line no-unused-vars
    private readonly network: TWalletNetwork
  ) {
    this.config = configs[implementation]
  }

  async init(onDeleteAdapter?: () => void) {
    this.detachListeners()
    this.onContextChange = () => {
      this.address = null
      onDeleteAdapter?.()
    }

    this.detachContextListeners = registerNeolineContextListeners(
      this.onContextChange
    )

    if (typeof window === 'undefined') {
      throw SdkNotInitializedError(WalletPlatform.NEOLINE)
    }

    await this.ensureSdkReady()
  }

  async restoreSession() {
    this.onAnotherNetwork = false

    if (await this.walletIsOnTheOtherChainFamily()) {
      this.address = null
      this.onAnotherNetwork = true

      return
    }

    await this.syncAddressFromSdk(false)
  }

  /** The last restore found the wallet elsewhere, not without an account for us. */
  restoreBlockedByNetwork() {
    return this.onAnotherNetwork
  }

  isInstalled() {
    if (typeof window === 'undefined') {
      return false
    }

    return typeof (window as any)[this.config.globalKey] !== 'undefined'
  }

  async connect() {
    if (!this.sdk) {
      try {
        await this.ensureSdkReady()
      } catch {
        throw SdkNotInitializedError(WalletPlatform.NEOLINE)
      }
    }

    await this.syncAddressFromSdk(true)

    if (!this.address) {
      throw RejectedByUser
    }

    this.onAnotherNetwork = false

    return this.getConnectedProvider()
  }

  getConnectedProvider(): NeolineExtensionProviderContext | null {
    if (!this.sdk) {
      return null
    }

    return {
      type: 'neoline-extension',
      sdk: this.sdk,
      address: this.address,
    }
  }

  async disconnect() {
    this.address = null
    this.onAnotherNetwork = false
  }

  async ensureNetwork(): Promise<void> {
    if (!this.sdk) {
      throw SdkNotInitializedError(WalletPlatform.NEOLINE)
    }

    let report = await this.readNetworkReport()

    if (
      !this.isOnExpectedNetwork(report.chainId, report.networkName) &&
      (await this.requestNetworkSwitch())
    ) {
      report = await this.awaitExpectedNetwork(3000)
    }

    validateNeoLineExtensionChainId(
      report.chainId,
      this.implementation,
      this.network,
      report.networkName
    )
  }

  /**
   * Restoring a stored session must stay silent: nobody asked to connect, the page
   * only reloaded. Reading the account is a dAPI call, and NeoLine answers a dAPI
   * call coming from the other chain family with its own "switch the network?"
   * window — that is the window that greets a reload. So ask `eth_chainId` first,
   * a plain read that opens nothing; when it points at Neo X while this connection
   * speaks N3, the Neo X dAPI confirms it, since that read lags behind a wallet
   * that has moved back to N3 and it is on the same side the reading claims.
   */
  private async walletIsOnTheOtherChainFamily(): Promise<boolean> {
    const evmFamily = neoLineChainFamily(await readNeoLineEvmChainId())

    if (evmFamily === null || evmFamily === this.implementation) return false
    if (evmFamily === 'n3') return true

    const report = await readNeoLineNeoXDapiNetwork()
    const dapiFamily =
      neoLineChainFamily(report.chainId) ??
      neoLineNameFamily(report.networkName)

    return dapiFamily !== this.implementation
  }

  private detachListeners() {
    this.detachContextListeners?.()
    this.detachContextListeners = undefined
    this.onContextChange = undefined
  }

  private async ensureSdkReady(): Promise<void> {
    await this.waitForGlobal(15000)

    if (this.tryInitializeSdk()) {
      return
    }

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(SdkNotInitializedError(WalletPlatform.NEOLINE))
      }, 10000)

      const onReady = () => {
        try {
          clearTimeout(timeout)
          window.removeEventListener(this.config.readyEvent, onReady)

          if (!this.tryInitializeSdk()) {
            reject(SdkNotInitializedError(WalletPlatform.NEOLINE))
          } else {
            resolve()
          }
        } catch {
          reject(SdkNotInitializedError(WalletPlatform.NEOLINE))
        }
      }

      window.addEventListener(this.config.readyEvent, onReady)
    })
  }

  private async waitForGlobal(timeoutMs: number): Promise<void> {
    const start = Date.now()

    while (Date.now() - start < timeoutMs) {
      if (typeof (window as any)[this.config.globalKey] !== 'undefined') {
        return
      }

      await new Promise(resolve => setTimeout(resolve, 200))
    }
  }

  private tryInitializeSdk(): boolean {
    const neoLineGlobal = (window as any)[this.config.globalKey]

    if (typeof neoLineGlobal === 'undefined') {
      return false
    }

    this.sdk = new neoLineGlobal.Init()

    return true
  }

  private async syncAddressFromSdk(validateNetwork: boolean) {
    if (!this.sdk) {
      this.address = null
      return
    }

    try {
      if (validateNetwork) {
        await this.ensureNetwork()
      } else {
        const report = await this.readNetworkReport()

        if (!this.isOnExpectedNetwork(report.chainId, report.networkName)) {
          this.address = null
          this.onAnotherNetwork = true

          return
        }
      }

      const {address} = await this.sdk.getAccount()

      if (validateNetwork) {
        if (!address) {
          this.address = null
          return
        }

        validateNeoLineAddressNetwork(address, this.implementation)
        this.address = address
        return
      }

      this.address = address ?? null
    } catch (error) {
      this.address = null

      if (validateNetwork) {
        throw error
      }
    }
  }

  private async readNetworkReport(): Promise<NeolineNetworkReport> {
    const networks = await this.sdk.getNetworks()

    return {
      chainId: networks?.chainId ?? networks?.chainid,
      networkName: networks?.defaultNetwork ?? networks?.defaultnetwork,
    }
  }

  private async awaitExpectedNetwork(
    timeoutMs: number
  ): Promise<NeolineNetworkReport> {
    const start = Date.now()

    let report = await this.readNetworkReport()

    while (
      !this.isOnExpectedNetwork(report.chainId, report.networkName) &&
      Date.now() - start < timeoutMs
    ) {
      await new Promise(resolve => setTimeout(resolve, 200))

      report = await this.readNetworkReport()
    }

    return report
  }

  private async requestNetworkSwitch(): Promise<boolean> {
    const switchNetwork =
      this.sdk?.switchWalletNetwork ?? this.sdk?.switchNetwork

    if (typeof switchNetwork !== 'function') return false

    const chainId = expectedNeoLineChainId(this.implementation, this.network)

    for (const params of [{chainId}, chainId] as const) {
      try {
        await switchNetwork.call(this.sdk, params)

        return true
      } catch (error) {
        if (isNeolineRejection(error)) return false
      }
    }

    return false
  }

  private isOnExpectedNetwork(
    chainId: number | undefined,
    networkName: string | undefined
  ): boolean {
    if (chainId === expectedNeoLineChainId(this.implementation, this.network)) {
      return true
    }

    if (chainId !== undefined && chainId !== null) {
      return false
    }

    return (
      neoLineNetworkNameLabel(networkName) ===
      neoLineTargetLabel(this.implementation, this.network)
    )
  }
}
